import { required, requiredIf } from "vuelidate/lib/validators";
import { store as canvasDesignStore } from "../store/canvasDesignStore";
import queryLogic from "./queryLogic";

const reducer = (res, val, ind, arr) => ind ? ind === arr.length - 1 ? `${res}, and ${val}` : `${res}, ${val}` : val;
/* workspace */
const workspaceNameSpecialCharacters = '.&+_-';
const workspaceNameFormatRegExp = new RegExp(`^[a-zA-Z0-9 ${workspaceNameSpecialCharacters}]+$`, 'g');
/* module */
const moduleFormatRestriction = ["<", ">", "|", '"', "`", "?", "*", ".", ":"];
/* information */
const informationFormatRestriction = ["<", ">", "|", '"', "`"];
const informationReservedKeywords = [
  "id",
  "assign to",
  "created by",
  "last change by",
];
const informationReservedFieldName = [
  ...queryLogic.querySelectionPrefix.map(x => x.fieldName),
];
/* activity */
const activityReservedKeywords = [
  "create",
  "edit",
  "update",
  "view",
  "delete",
  "change status",
  "duplicate",
  "comment",
  "assign",
];

/* validator */
const workspaceNameFormat = (val) => val ? Boolean(val.match(workspaceNameFormatRegExp)) : true;

const moduleDesign = (list) => {
  return {
    name: {
      required,
      unique: (val, item) => {
        if (val && Array.isArray(list))
          return !list.some((x) =>
            x.id != item.id &&
            x.name && x.name.toString().toLowerCase().trim() === val.toString().toLowerCase().trim()
          );
        else
          return true;
      },
      format: (val) => val ?
        !moduleFormatRestriction.some(
          (x) => val.toLowerCase().indexOf(x) > -1
        ) : true,
    },
  };
};

const informationDesign = (list) => {
  return {
    displayName: {
      required,
      unique: (val, item) => {
        const parentFields = list ?
          Array.isArray(list) ? list : []
          : item && Array.isArray(item.fields) ? item.fields : null;

        if (val && Array.isArray(parentFields))
          return !list.some((x) =>
            x.id !== item.id &&
            x.displayName && x.displayName.toString().toLowerCase().trim() === val.toString().toLowerCase().trim()
          );
        else
          return true;
      },
      format: (val) => val ?
        !informationFormatRestriction.some(
          (x) => val.toLowerCase().indexOf(x) > -1
        ) : true,
      nonReserved: (val) => val ?
        !informationReservedKeywords.some(
          (x) => x === val.toString().toLowerCase().trim()
        ) : true,
    },
    fieldName: {
      required: requiredIf(() => canvasDesignStore.state.beta),
      // nonReserved: (val) => val && canvasDesignStore.state.beta ?
      //   informationReservedFieldName.indexOf(val) === -1
      //   : true,
    },
    type: { required },
    connectionId: {
      required: requiredIf((x) =>
        !![7, 8, 20, 24].find((y) => y == x.type)
      ),
    },
    connectionField: {
      required: requiredIf((x) =>
        !![7, 8, 20, 24].find((y) => y == x.type)
      ),
    },
    fields: {
      required: requiredIf((x) => (x.type == 14)),
      $each: {
        ...informationDesign,
        $trackBy: "id",
      },
    },
    referenceModuleFieldId: {
      required: requiredIf((x) => x.type == 27 && x.reference),
    },
    referenceModuleTable: {
      required: requiredIf((x) => x.type == 27 && x.reference),
    },
    referenceDisplayField: {
      required: requiredIf((x) => x.type == 27 && x.reference),
    },
  };
};

const notContainsEmptyOrAnd = (filterItem) => {
  if(filterItem.type == 'or' || filterItem.type == 'and') {
    if(!filterItem.items || filterItem.items.length == 0) {
      return false;
    } else {
      return filterItem.items.every(x => notContainsEmptyOrAnd(x));
    }
  } 

  return true;
}

const menuDesign = (list) => {
  return {
    name: {
      required,
      unique: (val, item) => {
        if (val && Array.isArray(list))
          return !list.some((x) =>
            x.id !== item.id &&
            x.name && x.name.toString().toLowerCase().trim() === val.toString().toLowerCase().trim()
          );
        else
          return true;
      },
    },
    filter: {
      emptyCondition: (val, item) => {
        if (val && val.items) {
          return val.items.every(x => notContainsEmptyOrAnd(x));
        }
      }
    }
  };
};

const documentDesign = (list) => {
  return {
    name: {
      required,
      unique: (val, item) => {
        if (val && Array.isArray(list))
          return !list.some((x) =>
            x.id !== item.id &&
            x.name && x.name.toString().toLowerCase().trim() === val.toString().toLowerCase().trim()
          );
        else
          return true;
      },
    },
    logicServiceName: {
      required: requiredIf((val) => val.dataSource === 'Logic Service'),
    },
  };
};

const formDesign = {
  design: {
    rows: {
      required: (val) => {
        if (val && (!val || !val.length || val.some(x => x.items && !x.items.length)))
          return false;

        return true;
      }
    }
  }
};

const standardDesign = () => {
  return {
    ...formDesign,
  }
}

const activityDesign = (list) => {
  return {
    name: {
      required,
      unique: (val, item) => {
        if (val && Array.isArray(list))
          return !list.some((x) =>
            x.id !== item.id &&
            x.name && x.name.toString().toLowerCase().trim() === val.toString().toLowerCase().trim()
          );
        else
          return true;
      },
      nonReserved: (val) => val ?
        !activityReservedKeywords.some(
          (x) => x === val.toString().toLowerCase().trim()
        ) : true,
    },
    ...formDesign,
  };
};

const stateDesign = (list) => {
  return {
    name: {
      required,
      unique: (val, item) => {
        if (val && Array.isArray(list))
          return !list.some((x) =>
            x.id !== item.id &&
            x.name && x.name.toString().toLowerCase().trim() === val.toString().toLowerCase().trim()
          );
        else
          return true;
      },
    }
  };
};

const flowDesign = (list) => {
  return {
    fromStateId: {
      required: requiredIf((val) =>
        !val.activityId && !val.conditional
      ),
    },
    activityId: {
      required: requiredIf((val) =>
        !val.fromStateId && !val.conditional
      ),
    },
    conditionalStates: {
      required: requiredIf((val) => val.conditional),
      $each: {
        unique: (item, collection) => {
          if (item.toStateId && Array.isArray(collection))
            return collection.filter((x) =>
              x.toStateId === item.toStateId
            ).length === 1;
          else
            return true;
        },
        toStateId: { required },
        $trackBy: "id",
      },
    }
  };
};

const actionDesign = (list) => {
  return {
    name: {
      required,
      unique: (val, item) => {
        if (val && Array.isArray(list))
          return !list.some((x) =>
            x.id !== item.id &&
            x.name && x.name.toString().toLowerCase().trim() === val.toString().toLowerCase().trim()
          );
        else
          return true;
      },
    }
  };
};

const logicServiceDesign = (list) => {
  return {
    name: {
      required,
      unique: (val, item) => {
        if (val && Array.isArray(list))
          return !list.some((x) =>
            x.id !== item.id &&
            x.name && x.name.toString().toLowerCase().trim() === val.toString().toLowerCase().trim()
          );
        else
          return true;
      },
    }
  };
};

/* error */
function getErrorMessage(errors) {
  return (Array.isArray(errors) ? errors : []).reduce(reducer, null);
}

const moduleDesignError = (validate, key) => {
  const validator = validate && validate[key];
  if (validator && validator.$error) {
    if (key === "name") {
      if (!validator.unique) {
        return `${validate.$model.name || 'Name'} is already being used.`;
      } else if (!validator.format) {
        return `Oops! you cannot use these symbols ${getErrorMessage(moduleFormatRestriction)}`;
      } else {
        return "Module name is required.";
      }
    }
  }
  return false;
};

const informationDesignError = (validate, key) => {
  const validator = validate && validate[key];
  if (validator && validator.$error) {
    if (key === "displayName") {
      if (!validator.unique)
        return `${validate.$model.displayName || 'Name'} is already being used.`;
      else if (!validator.format)
        return `Oops! you cannot use these symbols ${getErrorMessage(informationFormatRestriction)}`;
      else if (!validator.nonReserved)
        return `Oops! you cannot use reserved words: ${getErrorMessage(informationReservedKeywords)}.`;
      else
        return "Information name is required.";
    } else if (key === "fieldName") {
      if (!validator.nonReserved)
        return `Oops! you cannot use reserved words: ${getErrorMessage(informationReservedFieldName)}.`;
      else
        return "Field Name is required.";
    } else if (key === "connectionId") {
      return "Module is required.";
    } else if (key === "connectionField") {
      return "Display information is required.";
    } else if (key === "fields") {
      return "Must have at least 1 column.";
    }
  }
  return false;
};

const menuDesignError = (validate, key) => {
  const validator = validate && validate[key];
  if (validator && validator.$error) {
    if (key === "name") {
      if (!validator.unique)
        return `${validate.$model.name || 'Name'} is already being used.`;
      else
        return "Listing name is required.";
    }
    if (key === 'filter') {
      return 'Filter(s) can not contains empty Or/And conditions';
    }
  }
  return false;
};

const documentDesignError = (validate, key) => {
  const validator = validate && validate[key];
  if (validator && validator.$error) {
    if (key === "name") {
      if (!validator.unique)
        return `${validate.$model.name || 'Name'} is already being used.`;
      else
        return "Template name is required.";
    }
  }
  return false;
};

const activityDesignError = (validate, key) => {
  const validator = validate && validate[key];
  if (validator && validator.$error) {
    if (key === "name") {
      if (!validator.unique)
        return `${validate.$model.name || 'Name'} is already being used.`;
      else if (!validator.nonReserved)
        return `Oops! you cannot use reserved words: ${getErrorMessage(activityReservedKeywords)}.`;
      else
        return "Activity name is required.";
    }
  }
  return false;
};

const stateDesignError = (validate, key) => {
  const validator = validate && validate[key];
  if (validator && validator.$error) {
    if (key === "name") {
      if (!validator.unique)
        return `${validate.$model.name || 'Name'} is already being used.`;
      else
        return "State name is required.";
    }
  }
  return false;
};

const flowDesignError = (validate, key) => {
  const validator = validate && validate[key];
  if (validator && validator.$error) {
    if (key === "fromStateId" || key === "activityId")
      return "From State and Activity, either one is required.";
    else if (key === "conditionalStates") {
      const conditionalStatesList = Object.values(validator.$each.$iter).filter(y => y.$invalid);
      if (!conditionalStatesList.every(x => x.toStateId.required))
        return "Conditional state is required."
      else if (!conditionalStatesList.every(x => x.unique))
        return "Conditional state must be unique."
      else
        return "Must have at least 1 conditional state.";
    }
  }
  return false;
};

const actionDesignError = (validate, key) => {
  const validator = validate && validate[key];
  if (validator && validator.$error) {
    if (key === "name") {
      if (!validator.unique)
        return `${validate.$model.name || 'Name'} is already being used.`;
      else
        return "Action name is required.";
    }
  }
  return false;
};

const logicServiceDesignError = (validate, key) => {
  const validator = validate && validate[key];
  if (validator && validator.$error) {
    if (key === "name") {
      if (!validator.unique)
        return `${validate.$model.name || 'Name'} is already being used.`;
      else
        return "Logic name is required.";
    }
  }
  return false;
};

/* translate validations */
const translateValidations = (validations, isError) => {
  try {
    const arr = Object.keys(validations).map((key) => ({ key:key, value: validations[key] }));
    if (isError) return arr.filter(x => x.value.$error);
    return arr;
  } catch(err) {
    return `Error occur. ${err}`;
  }
};

/* field validator */
/**
 *
 * @param {Object} validator
 * @param {String || Number} fieldId
 * @param {String} fieldName
 * @returns {Object}
 */
function fieldValidator(validator, fieldId, fieldName) {
  try {
    const validations = Object.values(validator);
    return validations.find((x) => x.$model.id == fieldId)[fieldName];
  } catch(err) {
    console.error("DesignValidator:FieldValidator", err);
  }
}
/**
 *
 * @param {Object} validator
 * @param {String || Number} fieldId
 * @param {String} fieldName
 */
const fieldValidatorTouch = (validator, fieldId, fieldName) => {
  let field = fieldValidator(validator, fieldId, fieldName);
  field && field.$touch();
}
/**
 *
 * @param {Object} validator
 * @param {String || Number} fieldId
 * @param {String} fieldName
 * @returns {Boolean}
 */
const fieldValidatorInvalid = (validator, fieldId, fieldName) => {
  const field = fieldValidator(validator, fieldId, fieldName);
  return field && field.$invalid && field.$dirty;
}

export default {
  workspaceNameSpecialCharacters: workspaceNameSpecialCharacters,
  informationFormatRestriction: informationFormatRestriction,
  informationReservedKeywords: informationReservedKeywords,
  informationReservedFieldName: informationReservedFieldName,
  activityReservedKeywords: activityReservedKeywords,
  /* validator */
  workspaceNameFormat: workspaceNameFormat,
  moduleDesign: moduleDesign,
  informationDesign: informationDesign,
  menuDesign: menuDesign,
  documentDesign: documentDesign,
  standardDesign: standardDesign,
  activityDesign: activityDesign,
  stateDesign: stateDesign,
  flowDesign: flowDesign,
  actionDesign: actionDesign,
  logicServiceDesign: logicServiceDesign,
  /* error */
  moduleDesignError: moduleDesignError,
  informationDesignError: informationDesignError,
  menuDesignError: menuDesignError,
  documentDesignError: documentDesignError,
  activityDesignError: activityDesignError,
  stateDesignError: stateDesignError,
  flowDesignError: flowDesignError,
  actionDesignError: actionDesignError,
  logicServiceDesignError: logicServiceDesignError,
  /* translate validations */
  translateValidations: translateValidations,
  /* field validator */
  fieldValidatorTouch: fieldValidatorTouch,
  fieldValidatorInvalid: fieldValidatorInvalid,
}